/**
 * Command Bar Fast Path: commands parsed locally, instantly and offline, in English or Spanish.
 * Anything it does not understand is returned as `unknown` for the LLM path.
 */

export interface TaskRef {
  id: string;
  name: string;
}

export interface ProjectRef {
  id: string;
  name: string;
  clientName: string | null;
  /** Open Tasks that commands can name, most recently used first. */
  tasks?: readonly TaskRef[];
}

export type Period = "today" | "week" | "month";

export type Intent =
  | { kind: "stop" }
  /** `newTask` names a Task to create in the Project for this time: "start acme r4 session as a new task". */
  | { kind: "start"; projectId: string | null; taskId: string | null; description: string; newTask?: string }
  | { kind: "log"; projectId: string | null; taskId: string | null; description: string; dayOffset: number; minutes: number; newTask?: string }
  | { kind: "log"; projectId: string | null; taskId: string | null; description: string; dayOffset: number; startMinute: number; endMinute: number; newTask?: string }
  /** A planned Event. No times means all day; `day` is days ahead or the next given weekday. */
  | { kind: "plan"; projectId: string | null; taskId: string | null; description: string; day: { offset: number } | { weekday: number }; startMinute: number | null; endMinute: number | null }
  /** Open the Calendar: a view (null keeps the default), periods ahead or back, or a month by name. */
  | { kind: "calendar"; view: "day" | "week" | "month" | null; offset: number; month: number | null }
  | { kind: "earnings"; period: Period }
  | { kind: "today" }
  /** Open Tasks, for every Project or the one named. */
  | { kind: "tasks"; projectId: string | null }
  /** Projects still in progress. */
  | { kind: "projects" }
  | { kind: "invoices"; status: "unpaid" | "overdue" | "draft" | null }
  | { kind: "uninvoiced" }
  /** Planned Events from now on. */
  | { kind: "events" }
  | { kind: "unknown"; text: string };

export interface ParseContext {
  /** Candidate projects, most recently used first; ties go to the earlier one. */
  projects: readonly ProjectRef[];
}

const STOP = /^(?:stop|para|parar|detener|deten|pausa|pause|done|listo)$/;
const START = /^(?:start|empieza|empezar|inicia|iniciar|comienza|comenzar|retoma|retomar|sigue|track)\b/;
const LOG_VERB = /\b(?:worked|work|log|logged|spent|trabaje|trabajo|registra|registrar|dedique|pase)\b/;
const DURATION = /(\d+(?:[.,]\d+)?)\s*(hours?|hrs?|horas?|h)(?:\s*(\d{1,2})\s*(?:minutes?|minutos?|mins?|m)?)?\b|(\d+)\s*(minutes?|minutos?|mins?|m)\b/;
const TIME = String.raw`(\d{1,2})(?::(\d{2}))?\s*(am|pm)?`;
const RANGE = new RegExp(String.raw`(?:\b(?:from|de|desde)\s+)?\b${TIME}\s*(?:-|\bto\b|\ba\b|\bhasta\b|\buntil\b)\s*${TIME}`);
/** "de la tarde", "in the morning", "esta noche": which half of the day a range is in. */
const PERIOD = /\b(?:de la|por la|en la|in the|this|esta)\s+(tarde|noche|afternoon|evening|night|manana|madrugada|morning)\b/;
const DAY = /\b(?:the\s+day\s+before\s+yesterday|anteayer|antier|yesterday|ayer|today|hoy)\b/;
/** "as a new task", "new task:", "como nueva tarea": the description names a Task to create. */
const NEW_TASK = /(?:\b(?:as\s+(?:an?\s+)?|como\s+(?:una\s+)?))?\b(?:new\s+task|nueva\s+tarea|tarea\s+nueva)\b:?/;
const PLAN = /^(?:plan|planea|planear|planifica|planificar|schedule|agendar|agendame|programa|programar)\b/;
const CALENDAR_WORD = /^(?:(?:open|show|go\s+to|abre|abrir|ver|muestra|mostrar)\s+)?(?:(?:the|my|el|mi)\s+)?(?:calendar|calendario|agenda)\b/;
/** "next week", "la semana que viene", "mes pasado": a period on its own opens the Calendar there. */
const PERIOD_ONLY = /^(?:(?:the|la|el|esta|este|this|next|last|previous|proxima|proximo|siguiente)\s+)?(?:week|month|semana|mes)(?:\s+(?:que\s+viene|pasada|pasado|anterior|siguiente|proxima|proximo))?$/;
const AHEAD = /\b(?:the\s+day\s+after\s+tomorrow|pasado\s+manana|tomorrow|manana|today|hoy|(?:(?:next|this|el|este|proximo)\s+)?(monday|tuesday|wednesday|thursday|friday|saturday|sunday|lunes|martes|miercoles|jueves|viernes|sabado|domingo))\b/;
const AT_TIME = new RegExp(String.raw`\b(?:at|a\s+las?)\s+${TIME}`);
const WEEKDAY_NUMBER: Record<string, number> = { sunday: 0, domingo: 0, monday: 1, lunes: 1, tuesday: 2, martes: 2, wednesday: 3, miercoles: 3, thursday: 4, jueves: 4, friday: 5, viernes: 5, saturday: 6, sabado: 6 };
const MONTH_NAMES = [["january", "enero"], ["february", "febrero"], ["march", "marzo"], ["april", "abril"], ["may", "mayo"], ["june", "junio"], ["july", "julio"], ["august", "agosto"], ["september", "septiembre", "setiembre"], ["october", "octubre"], ["november", "noviembre"], ["december", "diciembre"]];
const EARNINGS = /\b(?:how\s+much|cuanto|earn|earned|made|make|gane|ganado|ganancias?|ingresos?|income)\b/;
const TODAY_Q = /^(?:what\s+did\s+i\s+do\s+today|que\s+hice\s+hoy|today|hoy)\??$/;
/** How a lookup question may open: "do I have any", "show me my", "tengo", "cuáles son las". */
const ASK = String.raw`(?:(?:do\s+i\s+have|have\s+i\s+got|are\s+there|is\s+there|what\s+are|what'?s|show(?:\s+me)?|list|see|tengo|hay|cuales\s+son|muestra(?:me)?|ver|dame)\s+)?(?:(?:any|my|the|all|mis|las|los|todas\s+las|algunas?)\s+)*`;
const TASKS_Q = new RegExp(String.raw`^${ASK}(?:(?:pending|open|outstanding|remaining|unfinished)\s+)?(?:tasks?|to-?dos?|tareas?|pendientes)(?:\s+(?:pending|left|open|remaining|to\s+do|pendientes|abiertas|por\s+hacer))?(?:\s+(?:for|on|in|at|de|en|para|del)\s+(.+))?$`);
const PROJECTS_Q = new RegExp(String.raw`^${ASK}(?:(?:active|current|ongoing|activos|actuales)\s+)?(?:projects?|proyectos?)(?:\s+(?:in\s+progress|activos|actuales|en\s+curso))?$`);
const INVOICES_Q = new RegExp(String.raw`^${ASK}(?:(unpaid|overdue|outstanding|open|late|draft)\s+)?(?:invoices?|facturas?)(?:\s+(pendientes|vencidas|sin\s+pagar|por\s+cobrar|abiertas|borrador(?:es)?|unpaid|overdue))?$|^(?:who\s+owes\s+me(?:\s+money)?|quien\s+me\s+debe)$`);
const EVENTS_Q = new RegExp(String.raw`^${ASK}(?:(?:upcoming|next|future|planned|scheduled|proximos|proximas)\s+)?(?:events?|plans|meetings|eventos|reuniones|planes)(?:\s+(?:coming\s+up|ahead|proximos|proximas|planeados|agendados))?$|^(?:what'?s\s+(?:coming\s+up|next|planned|on\s+my\s+calendar)|que\s+tengo\s+(?:planeado|agendado|proximamente))$`);
const UNINVOICED_Q = new RegExp(String.raw`^${ASK}(?:(?:uninvoiced|unbilled|not\s+invoiced(?:\s+yet)?)(?:\s+(?:work|time|hours|money))?|(?:work|time|hours|money|trabajo|horas|tiempo)\s+(?:not\s+invoiced(?:\s+yet)?|sin\s+facturar|por\s+facturar|no\s+facturad[oa]s?)|sin\s+facturar|por\s+facturar)$|^(?:what\s+can\s+i\s+(?:bill|invoice)|que\s+puedo\s+facturar)$`);
/** Linking words around a Project name that never belong to the description: "fixes at acme". */
const FILLERS = new Set([
  "on", "at", "in", "into", "for", "to", "of", "from", "about", "with", "a", "the", "i", "working",
  "en", "para", "al", "con", "de", "del", "sobre", "por", "el", "la", "yo", "trabajando",
  "-", ":"
]);

/** Linking words that may open a description written after the Project: "acme on the login bug". */
const CONNECTORS = new Set(["on", "for", "to", "with", "a", "en", "para", "con", "al", "-", ":"]);

/** Words with the given linking words trimmed from both ends. */
function trimWords(text: string, linking: ReadonlySet<string>): string {
  const words = text.split(/\s+/).filter(Boolean);
  while (words.length && linking.has(normalize(words[0]!))) words.shift();
  while (words.length && linking.has(normalize(words[words.length - 1]!))) words.pop();
  return words.join(" ").replace(/^[:\-,\s]+|[:\-,\s]+$/g, "");
}

function normalize(text: string): string {
  return text.toLowerCase().normalize("NFD").replace(/\p{M}/gu, "");
}

/** Original text and its normalized twin, kept the same length so regex indices map back. */
class Source {
  readonly original: string;
  readonly text: string;
  private readonly used: boolean[];
  /** Where the named Project or Task sits, once found. */
  target: { start: number } | null = null;

  constructor(input: string) {
    this.original = input.normalize("NFC").trim().replace(/\s+/g, " ");
    const normalized = normalize(this.original);
    this.text = normalized.length === this.original.length ? normalized : normalize(this.original);
    this.used = Array.from({ length: this.text.length }, () => false);
  }

  /** Finds a pattern in the unused text and marks it used. */
  take(pattern: RegExp): RegExpExecArray | null {
    const match = pattern.exec(this.masked());
    if (match) this.mark(match.index, match[0].length);
    return match;
  }

  mark(index: number, length: number): void {
    for (let i = index; i < index + length; i += 1) this.used[i] = true;
  }

  /** Normalized text with used characters blanked out. */
  masked(): string {
    return [...this.text].map((char, index) => (this.used[index] ? " " : char)).join("");
  }

  /** Unused words with their positions. */
  words(): Array<{ word: string; index: number }> {
    const words: Array<{ word: string; index: number }> = [];
    for (const match of this.masked().matchAll(/[^\s]+/g)) words.push({ word: match[0], index: match.index });
    return words;
  }

  /**
   * The description: the remaining original text. Linking words are dropped where they lead into
   * the Project ("fixes at acme"), but after it only a connector is, so "acme about page" keeps
   * "about page".
   */
  rest(): string {
    const source = this.original.length === this.text.length ? this.original : this.text;
    const remaining = [...source].map((char, index) => (this.used[index] ? " " : char)).join("");
    // "…on andes, fixing the map": a comma or colon introduces the description.
    const separator = remaining.search(/[,:]/);
    if (separator >= 0 && remaining.slice(separator + 1).trim()) return trimWords(remaining.slice(separator + 1), CONNECTORS);
    if (!this.target) return trimWords(remaining, FILLERS);
    const before = trimWords(remaining.slice(0, this.target.start), FILLERS);
    const after = trimWords(remaining.slice(this.target.start), CONNECTORS);
    return [before, after].filter(Boolean).join(" ");
  }
}

function tokens(text: string): string[] {
  return normalize(text).split(/[^a-z0-9]+/).filter(Boolean);
}

type Word = { word: string; index: number };

const clean = (word: string) => word.replace(/[^a-z0-9]/g, "");

/** Whether a typed word names `name`: the same word or, from three letters, its start. */
function names(word: string, name: string): boolean {
  const typed = clean(word);
  return typed.length > 0 && (typed === name || (typed.length >= 3 && !FILLERS.has(typed) && name.startsWith(typed)));
}

/**
 * The typed words that name something called `parts`. Linking words ("about", "on", "de") only
 * count next to another word of the same name, so "about page" names About page while "notes on
 * acme" keeps "on" out of Acme. A name made only of linking words needs all of them in order.
 */
function nameHits(words: Word[], parts: string[]): Word[] {
  const core = parts.filter((part) => !FILLERS.has(part));
  if (!core.length) {
    for (let at = 0; at + parts.length <= words.length; at += 1) {
      const run = words.slice(at, at + parts.length);
      if (run.every((word, index) => clean(word.word) === parts[index])) return run;
    }
    return [];
  }
  const hits = new Set(words.filter((word) => word.word.length >= 2 && !FILLERS.has(word.word) && core.some((part) => names(word.word, part))));
  for (let grew = hits.size > 0; grew;) {
    grew = false;
    words.forEach((word, index) => {
      const linked = hits.has(words[index - 1]!) || hits.has(words[index + 1]!);
      if (!hits.has(word) && FILLERS.has(clean(word.word)) && parts.includes(clean(word.word)) && linked) {
        hits.add(word);
        grew = true;
      }
    });
  }
  return [...hits];
}

/**
 * Picks the Project, and optionally one of its Tasks, that best match the unused words, and
 * consumes them. A Project matches on any word of its name or Client; a Task only when every
 * word of its name is there, so a description that shares one word with a Task stays a
 * description. A Task alone is enough to find its Project.
 */
/** Real words count fully; linking words only half, so they settle ties but never outvote a name. */
function weigh(hits: Word[]): number {
  return hits.reduce((score, hit) => score + (FILLERS.has(clean(hit.word)) ? 0.5 : 1), 0);
}

function takeTarget(source: Source, projects: readonly ProjectRef[]): { projectId: string | null; taskId: string | null } {
  // Words after a colon are always description: "start avila: menu redesign".
  const colon = source.masked().indexOf(":");
  const words = source.words().filter(({ index }) => colon < 0 || index < colon);
  let best: { projectId: string; taskId: string | null; score: number; hits: Word[] } | null = null;
  const projectHitsOf = new Map(projects.map((project) => [project.id, nameHits(words, [...tokens(project.name), ...(project.clientName ? tokens(project.clientName) : [])])]));
  for (const project of projects) {
    const projectHits = projectHitsOf.get(project.id)!;
    if (projectHits.length > 0 && (!best || weigh(projectHits) > best.score)) best = { projectId: project.id, taskId: null, score: weigh(projectHits), hits: projectHits };
    for (const task of project.tasks ?? []) {
      const parts = tokens(task.name);
      const taskHits = nameHits(words, parts);
      const core = parts.filter((part) => !FILLERS.has(part));
      if (!taskHits.length || !core.every((part) => taskHits.some((hit) => names(hit.word, part)))) continue;
      // A Task that only repeats the Project's own words adds nothing to go on.
      if (taskHits.every((hit) => projectHits.includes(hit))) continue;
      // Once another Project is named by words outside this Task's name, that Project is meant:
      // "unisaldos about page" never reaches for Autocardve's About page.
      const elsewhere = projects.some((other) => other.id !== project.id && projectHitsOf.get(other.id)!.some((hit) => !FILLERS.has(clean(hit.word)) && !taskHits.includes(hit) && !projectHits.includes(hit)));
      if (elsewhere) continue;
      const hits = [...new Set([...projectHits, ...taskHits])];
      if (!best || weigh(hits) > best.score) best = { projectId: project.id, taskId: task.id, score: weigh(hits), hits };
    }
  }
  if (!best) return { projectId: null, taskId: null };
  for (const hit of best.hits) source.mark(hit.index, hit.word.length);
  source.target = { start: Math.min(...best.hits.map((hit) => hit.index)) };
  return { projectId: best.projectId, taskId: best.taskId };
}

/** Projects without their Tasks, for commands that name a Task to create rather than one to find. */
function withoutTasks(projects: readonly ProjectRef[]): ProjectRef[] {
  return projects.map((project) => ({ ...project, tasks: [] }));
}

/** The intent with its description moved to `newTask` when the command asked for a new Task. */
function asNewTask<T extends Intent & { description: string }>(intent: T, requested: boolean): T {
  return requested && intent.description ? { ...intent, newTask: intent.description, description: "" } : intent;
}

function toMinute(hour: string, minute: string | undefined, meridiem: string | undefined): number | null {
  let h = Number(hour);
  const m = minute ? Number(minute) : 0;
  if (h > 23 || m > 59) return null;
  if (meridiem === "pm" && h < 12) h += 12;
  if (meridiem === "am" && h === 12) h = 0;
  return h * 60 + m;
}

function dayOffsetOf(word: string | undefined): number {
  if (!word) return 0;
  if (/anteayer|antier|before/.test(word)) return -2;
  if (/yesterday|ayer/.test(word)) return -1;
  return 0;
}

/** Whether a range or time without am/pm means the afternoon: early hours like "3" do. */
function afternoonHour(hour: string, period: RegExpExecArray | null): boolean {
  if (period) return /tarde|noche|afternoon|evening|night/.test(period[1]!);
  return Number(hour) >= 1 && Number(hour) <= 6;
}

function parseCalendar(text: string): Intent | null {
  const named = CALENDAR_WORD.exec(text);
  if (!named && !PERIOD_ONLY.test(text)) return null;
  const rest = named ? text.slice(named[0].length) : text;
  const month = MONTH_NAMES.findIndex((names) => names.some((name) => new RegExp(String.raw`\b${name}\b`).test(rest)));
  const view = month >= 0 || /\b(?:month|mes)\b/.test(rest) ? "month" : /\b(?:day|dia|today|hoy|tomorrow|manana|yesterday|ayer)\b/.test(rest) ? "day" : /\b(?:week|semana)\b/.test(rest) ? "week" : null;
  const offset = /\b(?:next|proxim[oa]|siguiente|que\s+viene|tomorrow|manana)\b/.test(rest) ? 1 : /\b(?:last|previous|pasad[oa]|anterior|yesterday|ayer)\b/.test(rest) ? -1 : 0;
  return { kind: "calendar", view, offset, month: month >= 0 ? month : null };
}

function invoiceStatus(word: string | undefined): "unpaid" | "overdue" | "draft" | null {
  if (!word) return null;
  if (/overdue|late|vencidas/.test(word)) return "overdue";
  if (/draft|borrador/.test(word)) return "draft";
  return "unpaid";
}

/** Common lookup questions answered on the device, so they never wait for the model. */
function parseLookup(text: string, context: ParseContext): Intent | null {
  const question = text.replace(/^[¿¡\s]+/, "");
  const tasks = TASKS_Q.exec(question);
  if (tasks) {
    if (!tasks[1]) return { kind: "tasks", projectId: null };
    const named = new Source(tasks[1]);
    const { projectId } = takeTarget(named, context.projects);
    // Anything besides the Project ("tasks for next week") is for the model to read.
    return projectId && !named.rest() ? { kind: "tasks", projectId } : null;
  }
  if (PROJECTS_Q.test(question)) return { kind: "projects" };
  const invoices = INVOICES_Q.exec(question);
  if (invoices) return { kind: "invoices", status: /owes|debe/.test(question) ? "unpaid" : invoiceStatus(invoices[1] ?? invoices[2]) };
  if (UNINVOICED_Q.test(question)) return { kind: "uninvoiced" };
  if (EVENTS_Q.test(question)) return { kind: "events" };
  return null;
}

export function parseCommand(input: string, context: ParseContext): Intent {
  const source = new Source(input);
  const text = source.text.replace(/[?!.]+$/, "").trim();
  if (!text) return { kind: "unknown", text: input };
  if (STOP.test(text)) return { kind: "stop" };
  if (TODAY_Q.test(text)) return { kind: "today" };
  const lookup = parseLookup(text, context);
  if (lookup) return lookup;
  if (EARNINGS.test(text)) {
    const period: Period = /\b(?:today|hoy)\b/.test(text) ? "today" : /\b(?:week|semana)\b/.test(text) ? "week" : "month";
    return { kind: "earnings", period };
  }

  if (source.take(PLAN)) {
    const period = source.take(PERIOD);
    const ahead = source.take(AHEAD);
    const range = source.take(RANGE);
    const at = range ? null : source.take(AT_TIME);
    const duration = source.take(DURATION);
    const { projectId, taskId } = takeTarget(source, context.projects);
    const description = source.rest();
    let day: { offset: number } | { weekday: number } = { offset: 0 };
    if (ahead?.[1]) day = { weekday: WEEKDAY_NUMBER[ahead[1]]! };
    else if (ahead && /after|pasado/.test(ahead[0])) day = { offset: 2 };
    else if (ahead && /tomorrow|manana/.test(ahead[0])) day = { offset: 1 };
    let startMinute: number | null = null, endMinute: number | null = null;
    if (range) {
      const implied = range[3] ?? range[6] ?? (afternoonHour(range[1]!, period) ? "pm" : period ? "am" : undefined);
      startMinute = toMinute(range[1]!, range[2], range[3] ?? implied);
      endMinute = toMinute(range[4]!, range[5], range[6] ?? implied);
      if (startMinute !== null && endMinute !== null && endMinute <= startMinute && !range[6] && endMinute + 720 > startMinute) endMinute += 720;
    } else if (at) {
      startMinute = toMinute(at[1]!, at[2], at[3] ?? (afternoonHour(at[1]!, period) ? "pm" : undefined));
      const minutes = duration ? (duration[1] ? Math.round(Number(duration[1].replace(",", ".")) * 60 + Number(duration[3] ?? 0)) : Number(duration[4])) : 60;
      endMinute = startMinute === null ? null : Math.min(24 * 60, startMinute + Math.max(5, minutes));
    }
    if ((range || at) && (startMinute === null || endMinute === null || endMinute <= startMinute)) return { kind: "unknown", text: input };
    return { kind: "plan", projectId, taskId, description, day, startMinute, endMinute };
  }

  const calendar = parseCalendar(text);
  if (calendar) return calendar;

  const start = source.take(START);
  if (start) {
    const fresh = source.take(NEW_TASK) !== null;
    const { projectId, taskId } = takeTarget(source, fresh ? withoutTasks(context.projects) : context.projects);
    return asNewTask({ kind: "start", projectId, taskId, description: source.rest() }, fresh);
  }

  const verb = source.take(LOG_VERB);
  const day = source.take(DAY);
  const period = source.take(PERIOD);
  const range = source.take(RANGE);
  const duration = range ? null : source.take(DURATION);
  if (!verb && !range && !duration) return { kind: "unknown", text: input };

  const dayOffset = dayOffsetOf(day?.[0]);
  const fresh = source.take(NEW_TASK) !== null;
  const { projectId, taskId } = takeTarget(source, fresh ? withoutTasks(context.projects) : context.projects);
  const description = source.rest();

  if (range) {
    const explicit = range[3] ?? range[6];
    // Without am/pm, a period word decides; otherwise early hours like "3 to 5" mean the afternoon.
    const afternoon = period ? /tarde|noche|afternoon|evening|night/.test(period[1]!) : !explicit && Number(range[1]) >= 1 && Number(range[1]) <= 6;
    const implied = explicit ?? (period || afternoon ? (afternoon ? "pm" : "am") : undefined);
    const startMinute = toMinute(range[1]!, range[2], range[3] ?? implied);
    let endMinute = toMinute(range[4]!, range[5], range[6] ?? implied);
    if (startMinute === null || endMinute === null) return { kind: "unknown", text: input };
    if (endMinute <= startMinute && !range[6] && endMinute + 720 > startMinute) endMinute += 720;
    if (endMinute <= startMinute) return { kind: "unknown", text: input };
    return asNewTask({ kind: "log", projectId, taskId, description, dayOffset, startMinute, endMinute }, fresh);
  }
  if (duration) {
    const minutes = duration[1]
      ? Math.round(Number(duration[1].replace(",", ".")) * 60 + Number(duration[3] ?? 0))
      : Number(duration[4]);
    if (!(minutes > 0) || minutes > 24 * 60) return { kind: "unknown", text: input };
    return asNewTask({ kind: "log", projectId, taskId, description, dayOffset, minutes }, fresh);
  }
  return { kind: "unknown", text: input };
}
